import { useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import {
  validateFacebookOAuthConfig,
  generateFacebookOAuthPopupUrl,
  getFacebookOAuthConfigInfo,
  FACEBOOK_OAUTH_ENDPOINTS
} from '../config/facebookOAuth';
import { getUserTimezone } from '../utils/timezoneUtils';

const SESSION_KEY_FLOW = 'facebook_oauth_flow';
const SESSION_KEY_STATE = 'facebook_oauth_state';
const SESSION_KEY_USER_TYPE = 'facebook_oauth_userType';
const SESSION_KEY_RETURN_TO = 'facebook_oauth_returnTo';
const SESSION_KEY_RESULT = 'facebook_oauth_result';

// Mirrors useAppleOAuth.shouldUseRedirectFlow. window.open in an iOS RN
// WebView is blocked or has no opener, so the popup-based postMessage flow
// never completes. The redirect flow stays inside the WebView and needs no
// native-side support.
let isIOSDevice = false;
const shouldUseRedirectFlow = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  isIOSDevice = /iPhone|iPod|iPad/.test(ua);
  if ((window as any).ReactNativeWebView) return true;
  if (isIOSDevice) return true;
  if (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return true;
  return false;
};

interface FacebookUserInfo {
  id: string;
  name: string;
  email?: string; // Make email optional since we're not requesting it in scope
  first_name: string;
  last_name: string;
  picture?: {
    data: {
      url: string;
      width: number;
      height: number;
    };
  };
}

export const useFacebookOAuth = () => {
  const { socialLogin } = useAuth();
  const userTypeRef = useRef<number>(1);
  const callbacksRef = useRef<{
    onSuccess?: (_message?: string) => void;
    onError?: (_error: string) => void;
  }>({});
  const expectedStateRef = useRef<string | null>(null);
  // Cleanup for the in-progress OAuth flow. Set from inside `facebookLogin`,
  // called by `cancelOAuth`.
  const cleanupRef = useRef<(() => void) | null>(null);

  // Function to exchange authorization code for access token
  const exchangeCodeForToken = useCallback(async (code: string): Promise<string> => {
    try {
      console.log('Exchanging authorization code for access token...');

      // This should be done on your backend for security
      // For now, we'll make the request directly, but in production,
      // you should send the code to your backend
      const tokenResponse = await fetch(FACEBOOK_OAUTH_ENDPOINTS.token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: process.env.REACT_APP_FACEBOOK_CLIENT_ID || '960563116183235',
          client_secret: process.env.REACT_APP_FACEBOOK_CLIENT_SECRET || 'e162da768038eb0b5d58b390141877ed',
          redirect_uri: `${window.location.origin}${process.env.PUBLIC_URL}/facebook-oauth-callback.html`,
          code: code,
          grant_type: 'authorization_code'
        })
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        console.error('Token exchange failed:', {
          status: tokenResponse.status,
          statusText: tokenResponse.statusText,
          body: errorText
        });
        throw new Error(`Token exchange failed: ${tokenResponse.status} ${tokenResponse.statusText}`);
      }

      const tokenData = await tokenResponse.json();

      if (tokenData.error) {
        console.error('Facebook API error in token response:', tokenData.error);
        throw new Error(`Facebook API Error: ${tokenData.error.message} (Code: ${tokenData.error.code})`);
      }

      if (!tokenData.access_token) {
        console.error('No access token in response:', tokenData);
        throw new Error('No access token received from Facebook');
      }

      console.log('Successfully received access token');
      return tokenData.access_token;

    } catch (error) {
      console.error('Error exchanging code for token:', error);
      throw error;
    }
  }, []);

  const handleFacebookSuccess = useCallback(async (credentialResponse: any) => {
    try {

      // Validate that we have a real access token
      if (!credentialResponse.access_token || credentialResponse.access_token.startsWith('fb_mock_token_')) {
        throw new Error('Invalid or mock access token received from Facebook');
      }

      // Get user info from Facebook Graph API using config endpoint
      const response = await fetch(`${FACEBOOK_OAUTH_ENDPOINTS.userInfoWithFields}`, {
        headers: {
          Authorization: `Bearer ${credentialResponse.access_token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Facebook Graph API error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });

        // Try to parse error response
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            throw new Error(`Facebook API Error: ${errorData.error.message} (Code: ${errorData.error.code})`);
          }
        } catch (parseError) {
          // If we can't parse the error, use the raw text
          throw new Error(`Facebook API Error: ${errorText}`);
        }

        throw new Error('Failed to fetch user info from Facebook');
      }

      const userInfo: FacebookUserInfo = await response.json();

      // Generate a fallback email if not provided by Facebook
      const fallbackEmail = userInfo.email || `${userInfo.id}@facebook.com`;

      // Prepare data for social login
      const socialData = {
        loginType: 'facebook' as const,
        accessToken: credentialResponse.access_token,
        email: fallbackEmail,
        name: userInfo.name || 'Facebook User',
        picture: userInfo.picture?.data?.url,
        firstName: userInfo.first_name || 'Facebook',
        lastName: userInfo.last_name || 'User',
        userType: userTypeRef.current,
        timezone: getUserTimezone(),
      };

      // Call the social login function
      const result = await socialLogin(socialData);

      // Show success message based on backend response
      if (result.success) {
        console.log('Facebook login successful');
        const successMessage = (result as any).message || 'Account created successfully. Redirecting to dashboard...';
        callbacksRef.current.onSuccess?.(successMessage);
      } else {
        console.error('Facebook login failed:', result.message);
        callbacksRef.current.onError?.(result.error || 'Facebook login failed');
      }

      return result;
    } catch (error) {
      console.error('Facebook OAuth error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Facebook login failed';
      callbacksRef.current.onError?.(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [socialLogin]);

  const facebookLogin = useCallback(() => {
    if (!validateFacebookOAuthConfig()) {
      throw new Error('Facebook OAuth is not configured. Please set REACT_APP_FACEBOOK_CLIENT_ID in your .env file.');
    }

    const { url: authUrl, state } = generateFacebookOAuthPopupUrl();
    expectedStateRef.current = state;

    if (shouldUseRedirectFlow()) {
      try {
        sessionStorage.setItem(SESSION_KEY_FLOW, 'redirect');
        sessionStorage.setItem(SESSION_KEY_STATE, state);
        sessionStorage.setItem(SESSION_KEY_USER_TYPE, String(userTypeRef.current));
        sessionStorage.setItem(
          SESSION_KEY_RETURN_TO,
          window.location.pathname + window.location.search
        );

        // In the iOS RN WebView the redirect flow navigates away from this page,
        // so the popup/`popup.closed` cancellation path never runs. Instead the
        // native layer posts an OAUTH_CANCEL_ERROR message when the user taps the
        // Back button. Listen for it on both `window` and
        // `document` (RN dispatches the event on `document`), surface a
        // "cancelled" error, and detach both listeners so it only fires once.
        // Android's native layer doesn't post this message, so only attach on iOS.
        if (isIOSDevice) {
          const handleMessage = (event: MessageEvent) => {
            const data = typeof event.data == "string" ? JSON.parse(event.data) : event.data;

            if (data.type === 'OAUTH_CANCEL_ERROR') {
              callbacksRef.current.onError?.('Login was cancelled');

              window.removeEventListener('message', handleMessage);
              // @ts-ignore - RN dispatches the 'message' event on `document`, not `window`
              document.removeEventListener('message', handleMessage);
            }
          };

          window.addEventListener('message', handleMessage);
          // @ts-ignore - RN dispatches the 'message' event on `document`, not `window`
          document.addEventListener('message', handleMessage);
        }
      } catch {
        // sessionStorage may be unavailable in private mode; fall through to popup as a last resort.
      }
      window.location.assign(authUrl);
      return;
    }

    // Open popup window
    const popup = window.open(
      authUrl,
      'facebook-login',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }

    const isReactNativeWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;
    let settled = false;

    const cleanup = () => {
      settled = true;
      if (checkClosed) clearInterval(checkClosed);
      try { popup.close(); } catch {console.log("Error while closing popup")}
      window.removeEventListener('message', handleMessage);
      // @ts-ignore - RN dispatches custom event
      document.removeEventListener('message', handleMessage);
      cleanupRef.current = null;
    };

    const checkClosed = isReactNativeWebView
      ? null
      : setInterval(() => {
          if (popup.closed) {
            if (settled) {
              clearInterval(checkClosed as any);
              return;
            }
            cleanup();
            callbacksRef.current.onError?.('Login was cancelled');
          }
        }, 1000);

    const handleMessage = async (event: MessageEvent) => {
      if (settled) return;
      if (event.origin && event.origin !== window.location.origin) return;
      const data = typeof event.data == "string" ? JSON.parse(event.data) : event.data;

      if (data.type === 'OAUTH_SUCCESS') {
        const returnedState = data.state as string | undefined;
        if (expectedStateRef.current && returnedState && returnedState !== expectedStateRef.current) {
          cleanup();
          callbacksRef.current.onError?.('Invalid OAuth state. Please try again.');
          return;
        }
        cleanup();
        try {
          const { code } = data;
          const access_token = await exchangeCodeForToken(code);
          await handleFacebookSuccess({ access_token });
        } catch (error) {
          console.error('Failed to exchange code for token:', error);
          callbacksRef.current.onError?.(error instanceof Error ? error.message : 'Failed to complete Facebook login');
        }
      } else if (data.type === 'OAUTH_ERROR') {
        cleanup();
        callbacksRef.current.onError?.(data.error || 'Facebook login failed');
      }
    };

    window.addEventListener('message', handleMessage);
    // @ts-ignore - RN dispatches custom event
    document.addEventListener('message', handleMessage);

    cleanupRef.current = cleanup;
  }, [handleFacebookSuccess, exchangeCodeForToken]);

  const cancelOAuth = useCallback(() => {
    if (cleanupRef.current) cleanupRef.current();
  }, []);

  // Picks up the code stashed by the callback page after the iOS redirect flow,
  // exchanges it for an access_token, and completes socialLogin. Returns true
  // if a pending result was found and handled.
  const processRedirectResult = useCallback(
    (
      callbacks?: {
        onSuccess?: (_message?: string) => void;
        onError?: (_error: string) => void;
      }
    ): boolean => {
      if (typeof window === 'undefined') return false;

      let raw: string | null = null;
      try {
        raw = sessionStorage.getItem(SESSION_KEY_RESULT);
      } catch {
        return false;
      }
      if (!raw) return false;

      let expectedState: string | null = null;
      let storedUserType: string | null = null;
      try {
        sessionStorage.removeItem(SESSION_KEY_RESULT);
        expectedState = sessionStorage.getItem(SESSION_KEY_STATE);
        storedUserType = sessionStorage.getItem(SESSION_KEY_USER_TYPE);
        sessionStorage.removeItem(SESSION_KEY_STATE);
        sessionStorage.removeItem(SESSION_KEY_USER_TYPE);
        sessionStorage.removeItem(SESSION_KEY_FLOW);
        sessionStorage.removeItem(SESSION_KEY_RETURN_TO);
      } catch {
        /* ignore storage failures */
      }

      if (callbacks) callbacksRef.current = callbacks;

      if (storedUserType) {
        const ut = parseInt(storedUserType, 10);
        if (!Number.isNaN(ut)) userTypeRef.current = ut;
      }

      let parsed: { code?: string; state?: string; error?: string };
      try {
        parsed = JSON.parse(raw);
      } catch {
        callbacksRef.current.onError?.('Failed to process Facebook sign-in result.');
        return true;
      }

      if (parsed.error) {
        callbacksRef.current.onError?.(parsed.error);
        return true;
      }

      if (expectedState && parsed.state && parsed.state !== expectedState) {
        callbacksRef.current.onError?.('Invalid OAuth state. Please try again.');
        return true;
      }

      if (!parsed.code) {
        callbacksRef.current.onError?.('No authorization code received from Facebook');
        return true;
      }

      // Fire-and-forget: exchange code for token then complete socialLogin.
      // Errors flow through callbacksRef.current.onError so the caller's UI
      // settles correctly.
      (async () => {
        try {
          const access_token = await exchangeCodeForToken(parsed.code as string);
          await handleFacebookSuccess({ access_token });
        } catch (error) {
          console.error('Failed to exchange code for token:', error);
          callbacksRef.current.onError?.(error instanceof Error ? error.message : 'Failed to complete Facebook login');
        }
      })();
      return true;
    },
    [exchangeCodeForToken, handleFacebookSuccess]
  );

  const facebookLoginWithUserType = useCallback((userType: number, onSuccess?: (_message?: string) => void, onError?: (_error: string) => void) => {
    userTypeRef.current = userType;
    callbacksRef.current = { onSuccess, onError };
    return () => {
      facebookLogin();
    };
  }, [facebookLogin]);

  const isFacebookOAuthConfigured = useCallback(() => {
    return validateFacebookOAuthConfig();
  }, []);

  const getFacebookOAuthConfig = useCallback(() => {
    return getFacebookOAuthConfigInfo();
  }, []);

  return {
    facebookLoginWithUserType,
    isFacebookOAuthConfigured,
    getFacebookOAuthConfig,
    cancelOAuth,
    processRedirectResult,
  };
};
